/**
 * Application context: config, the signed-in user, reference data and the toast host.
 *
 * REFERENCE DATA IS LOADED ONCE, HERE. That is what makes "no hardcoded status enums in the
 * client" practical rather than chatty — every select in the app renders from this without its own
 * round trip, and adding a lead status in Administration needs no code change (Constitution 4.4).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { tfApi } from '../api/tf_api'
import { ApiError } from '../api/shared_api_client'
import type { AppConfig, ReferenceDomains, ReferenceValue } from '../types/tf_types'

export interface Toast {
  id: number
  tone: 'ok' | 'err' | 'info'
  title: string
  body?: string
  technical?: string
  /** Offer a retry. For failures that are usually transient and currently need a page reload. */
  retry?: boolean
}

interface AppContextValue {
  loading: boolean
  error: string | null
  /** Set when the app started but reference data did not load. Dropdowns will be empty. */
  referenceError: string | null
  config: AppConfig | null
  userId: string
  reference: ReferenceDomains
  /** Options for a domain, ready for a <select>. Empty array if the domain is unknown. */
  options: (domain: string) => ReferenceValue[]
  /** The indicator TOKEN for a value — never a colour. */
  indicatorFor: (domain: string, label: string | null | undefined) => string
  toasts: Toast[]
  notify: (t: Omit<Toast, 'id'>) => void
  notifyError: (title: string, err: unknown) => void
  dismiss: (id: number) => void
  reload: () => Promise<void>
}

const AppContext = createContext<AppContextValue | null>(null)

export function AppProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [userId, setUserId] = useState('tidalfin')
  const [reference, setReference] = useState<ReferenceDomains>({})
  const [referenceError, setReferenceError] = useState<string | null>(null)
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const boot = await tfApi.fetchBootstrap()
      setConfig(boot.config)
      setUserId(boot.user.userId)
      setReference(boot.referenceDomains ?? {})
      setReferenceError(boot.referenceError ?? null)
      if (boot.referenceError) {
        //  Surfaced rather than swallowed: an app running with no reference data renders empty
        //  dropdowns everywhere, and "the statuses are missing" is much harder to diagnose than a
        //  message saying so.
        //
        //  RECOVERABLE WITHOUT A RELOAD. The usual cause is transient — a database connection that
        //  went stale while idle — and a toast with no way to retry leaves the application
        //  permanently degraded until somebody thinks to refresh the page.
        setToasts((t) => [
          ...t,
          {
            id: nextId.current++,
            tone: 'err',
            title: 'Reference data unavailable',
            body: boot.referenceError ?? undefined,
            technical: boot.referenceErrorDetail ?? undefined,
            retry: true,
          },
        ])
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.detail.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const notify = useCallback((t: Omit<Toast, 'id'>) => {
    const id = nextId.current++
    setToasts((current) => [...current, { ...t, id }])
    //  Successes disappear on their own; errors stay until dismissed, because an error the user
    //  did not see is an error they cannot report.
    if (t.tone === 'ok' || t.tone === 'info') {
      setTimeout(() => setToasts((current) => current.filter((x) => x.id !== id)), 4000)
    }
  }, [])

  const notifyError = useCallback(
    (title: string, err: unknown) => {
      if (err instanceof ApiError) {
        notify({ tone: 'err', title, body: err.detail.message, technical: err.detail.technical })
      } else {
        notify({ tone: 'err', title, body: String(err) })
      }
    },
    [notify],
  )

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const options = useCallback(
    (domain: string) => reference[domain] ?? [],
    [reference],
  )

  const indicatorFor = useCallback(
    (domain: string, label: string | null | undefined) => {
      if (!label) return 'dormant'
      const match = (reference[domain] ?? []).find(
        (v) => v.ValueLabel.toLowerCase() === label.toLowerCase(),
      )
      return match?.IndicatorColor ?? 'neutral'
    },
    [reference],
  )

  const value = useMemo<AppContextValue>(
    () => ({
      loading, error, referenceError, config, userId, reference,
      options, indicatorFor, toasts, notify, notifyError, dismiss, reload: load,
    }),
    [loading, error, referenceError, config, userId, reference, options, indicatorFor, toasts,
     notify, notifyError, dismiss, load],
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used inside AppProvider')
  return ctx
}
